// FILE: src/components/AdminUsersScreen.js
//
// Admin Users management screen — Phase 6.
// Lists everyone in the admin_users table and provides Add / Remove flows
// for inviting new SMEs or removing departing ones, all without touching
// Netlify env vars or redeploying.
//
// ── Safety rails ─────────────────────────────────────────────────────────────
//
//   The Remove button is disabled (with a tooltip explaining why) when:
//     1. The row is the currently-signed-in user           ("can't remove yourself")
//     2. The row's is_bootstrap flag is TRUE               ("bootstrap, immutable")
//     3. The list contains only one removable row          ("must keep ≥1 admin")
//
//   The same checks are enforced server-side in migration 016's RPCs;
//   the UI guards just avoid the server round-trip on obvious-no operations.

import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AdminGateContext } from './AdminGate';
import {
  listAdminUsers,
  addAdminUser,
  removeAdminUser,
} from '../../services/adminUserService';
import { supabase } from '../../services/supabaseClient';

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  wrapper: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#1a1a2e',
    color: '#fff',
    padding: '16px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  headerTitle: {
    fontSize: '1.15rem',
    fontWeight: 'bold',
    flex: 1,
    letterSpacing: '0.5px',
  },
  body: {
    flex: 1,
    padding: '32px',
    maxWidth: '900px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '12px',
    marginTop: 0,
  },
  intro: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  card: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '20px 24px',
    marginBottom: '24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '2px solid #e0e0e0',
    color: '#555',
    fontWeight: '600',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #f0f0f0',
    color: '#333',
    verticalAlign: 'top',
  },
  emailCell: {
    fontFamily: 'monospace',
    fontSize: '0.88rem',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '0.7rem',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginLeft: '8px',
    background: '#e3f2fd',
    color: '#1565c0',
  },
  badgeSelf: {
    background: '#fff3e0',
    color: '#e65100',
  },
  removeButton: (disabled) => ({
    padding: '6px 12px',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: disabled ? '#aaa' : '#c62828',
    background: '#fff',
    border: `1px solid ${disabled ? '#ddd' : '#ef9a9a'}`,
    borderRadius: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  addCard: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '20px 24px',
    marginBottom: '24px',
  },
  addRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'flex-start',
  },
  input: {
    flex: '1 1 220px',
    minWidth: '220px',
    padding: '8px 10px',
    fontSize: '0.9rem',
    border: '1px solid #dadce0',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  notesInput: {
    flex: '2 1 280px',
    minWidth: '180px',
    padding: '8px 10px',
    fontSize: '0.9rem',
    border: '1px solid #dadce0',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  addButton: (busy) => ({
    padding: '8px 18px',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#fff',
    background: busy ? '#90a4ae' : '#1565c0',
    border: 'none',
    borderRadius: '4px',
    cursor: busy ? 'wait' : 'pointer',
    flexShrink: 0,
  }),
  feedback: (kind) => ({
    fontSize: '0.85rem',
    marginTop: '12px',
    color:
      kind === 'error' ? '#c62828' :
      kind === 'warn'  ? '#e65100' :
                         '#2e7d32',
  }),
  loading: {
    fontSize: '1rem',
    color: '#888',
    textAlign: 'center',
    padding: '40px',
  },
};

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Admin Users management screen.
 *
 * @param {function} onBack — return to the Admin hub
 */
export default function AdminUsersScreen({ onBack }) {
  const auth = useContext(AdminGateContext) || {};
  const callerEmail = (auth.userEmail || '').toLowerCase();

  const [admins,   setAdmins]   = useState(null);   // null = loading
  const [loadErr,  setLoadErr]  = useState(null);

  const [emailInput,  setEmailInput]  = useState('');
  const [notesInput,  setNotesInput]  = useState('');
  const [sendInvite,  setSendInvite]  = useState(true);
  const [addBusy,     setAddBusy]     = useState(false);
  const [addMsg,      setAddMsg]      = useState(null); // { kind, text }

  const [removingEmail, setRemovingEmail] = useState(null);

  // ── Load admins ──────────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoadErr(null);
    try {
      const rows = await listAdminUsers();
      setAdmins(rows);
    } catch (err) {
      setLoadErr(err.message || 'Failed to load admins');
      setAdmins([]);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Add (optionally with magic-link invitation) ──────────────────────────
  // The DB row is the source of truth — the OTP send is a best-effort
  // notification on top.  If the row insert succeeds but the OTP fails,
  // we keep the row and surface a partial-success message; the admin can
  // still share the URL manually.
  const handleAdd = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!email) return;

    setAddBusy(true);
    setAddMsg(null);
    try {
      await addAdminUser(email, notesInput.trim());
    } catch (err) {
      setAddMsg({ kind: 'error', text: err.message || 'Add failed' });
      setAddBusy(false);
      return;
    }

    // Row was inserted.  Refresh both the local list and the gate's cache.
    await refresh();
    if (auth.refreshAdmins) await auth.refreshAdmins();

    // Optional: send a magic-link invitation email.
    let inviteNote = '';
    if (sendInvite) {
      try {
        const { error: otpErr } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: window.location.origin },
        });
        if (otpErr) {
          inviteNote = ` — admin row saved, but invitation email failed: ${otpErr.message}`;
        } else {
          inviteNote = ' — invitation email sent';
        }
      } catch (otpErr) {
        inviteNote = ` — admin row saved, but invitation email failed: ${otpErr.message || 'unknown error'}`;
      }
    }

    setEmailInput('');
    setNotesInput('');
    setAddMsg({
      kind: inviteNote.includes('failed') ? 'warn' : 'ok',
      text: `Added ${email}${inviteNote}`,
    });
    setAddBusy(false);
  };

  // ── Remove ───────────────────────────────────────────────────────────────
  const handleRemove = async (email) => {
    if (!email) return;
    if (!window.confirm(`Remove admin access for ${email}?`)) return;

    setRemovingEmail(email);
    try {
      await removeAdminUser(email);
      await refresh();
      if (auth.refreshAdmins) await auth.refreshAdmins();
    } catch (err) {
      window.alert(err.message || 'Remove failed');
    } finally {
      setRemovingEmail(null);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const removableCount = (admins || []).filter((a) => !a.is_bootstrap).length;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-users-screen" style={styles.wrapper}>
      <div style={styles.header}>
        <button
          data-testid="admin-users-back"
          style={styles.backButton}
          onClick={onBack}
        >
          ← Back
        </button>
        <span style={styles.headerTitle}>👥 Admin Users</span>
      </div>

      <div style={styles.body}>
        <p style={styles.sectionTitle}>Manage admin access</p>
        <p style={styles.intro}>
          Add or remove email addresses that may sign in to the Admin overlay.
          Bootstrap admins are seeded at deploy time and cannot be removed here.
          You cannot remove your own access (sign out and ask another admin to do it).
        </p>

        {/* ── Add form ──────────────────────────────────────────────── */}
        <div style={styles.addCard}>
          <p style={styles.sectionTitle}>Add admin</p>
          <form onSubmit={handleAdd}>
            <div style={styles.addRow}>
              <input
                data-testid="admin-users-email-input"
                type="email"
                placeholder="reviewer@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                required
                disabled={addBusy}
                style={styles.input}
              />
              <input
                data-testid="admin-users-notes-input"
                type="text"
                placeholder="Notes (optional, e.g. PMP SME 2026 cohort)"
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                disabled={addBusy}
                style={styles.notesInput}
              />
              <button
                data-testid="admin-users-add-button"
                type="submit"
                disabled={addBusy || !emailInput.trim()}
                style={styles.addButton(addBusy)}
              >
                {addBusy ? 'Adding…' : 'Add admin'}
              </button>
            </div>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '10px',
                fontSize: '0.85rem',
                color: '#555',
                cursor: addBusy ? 'wait' : 'pointer',
              }}
            >
              <input
                data-testid="admin-users-send-invite"
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                disabled={addBusy}
              />
              Send invitation email (magic-link sign-in)
            </label>
            {addMsg && (
              <div data-testid="admin-users-add-feedback" style={styles.feedback(addMsg.kind)}>
                {addMsg.kind === 'error' ? '✗ ' : addMsg.kind === 'warn' ? '⚠ ' : '✓ '}{addMsg.text}
              </div>
            )}
          </form>
        </div>

        {/* ── List ──────────────────────────────────────────────────── */}
        <div style={styles.card}>
          <p style={styles.sectionTitle}>Current admins</p>
          {admins === null ? (
            <div style={styles.loading}>Loading…</div>
          ) : loadErr ? (
            <div style={styles.feedback('error')} data-testid="admin-users-load-error">
              ✗ {loadErr}
            </div>
          ) : admins.length === 0 ? (
            <div style={styles.loading}>No admins found.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Added</th>
                  <th style={styles.th}>By</th>
                  <th style={styles.th}>Notes</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody data-testid="admin-users-list">
                {admins.map((a) => {
                  const isSelf       = a.email.toLowerCase() === callerEmail;
                  const isLast       = !a.is_bootstrap && removableCount <= 1;
                  const removeReason =
                    a.is_bootstrap ? 'Bootstrap admin — cannot be removed'
                    : isSelf       ? "Can't remove yourself"
                    : isLast       ? 'Must keep at least one removable admin'
                                   : null;
                  const disableRemove = !!removeReason || removingEmail === a.email;

                  return (
                    <tr key={a.email} data-testid={`admin-users-row-${a.email}`}>
                      <td style={{ ...styles.td, ...styles.emailCell }}>
                        {a.email}
                        {a.is_bootstrap && (
                          <span style={styles.badge} data-testid="admin-users-bootstrap-badge">
                            Bootstrap
                          </span>
                        )}
                        {isSelf && (
                          <span style={{ ...styles.badge, ...styles.badgeSelf }} data-testid="admin-users-self-badge">
                            You
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>{fmtDate(a.added_at)}</td>
                      <td style={styles.td}>{a.added_by || '—'}</td>
                      <td style={styles.td}>{a.notes || '—'}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        <button
                          data-testid={`admin-users-remove-${a.email}`}
                          style={styles.removeButton(disableRemove)}
                          onClick={() => handleRemove(a.email)}
                          disabled={disableRemove}
                          title={removeReason || ''}
                        >
                          {removingEmail === a.email ? 'Removing…' : 'Remove'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
